import type { CombatFx } from '../../model/presentation'

/**
 * Presentation FX priority — higher survives budget pressure.
 * Essential mechanical cues must outrank floaters/particles.
 */
export type FxPriorityBand =
  | 'essential'
  | 'signature'
  | 'combat'
  | 'floater'
  | 'cosmetic'

export function fxPriorityBand(fx: CombatFx): FxPriorityBand {
  switch (fx.type) {
    case 'waveClear':
    case 'leak':
    case 'death':
    case 'shieldBreak':
    case 'deepFreezeFlash':
    case 'contagion':
      return 'essential'
    case 'burnApply':
    case 'armorBreakApply':
    case 'regenHeal':
    case 'statusReject':
      return 'signature'
    case 'splash':
      return fx.innerRadius !== undefined ? 'signature' : 'combat'
    case 'towerPulse':
      if (fx.kind !== 'fire') return 'essential'
      return fx.multiLock ? 'signature' : 'combat'
    case 'hitFlash':
    case 'impact':
      return fx.type === 'impact' && fx.signature ? 'signature' : 'combat'
    case 'damageNumber':
      if (fx.style === 'dot') return 'floater'
      if (fx.style === 'amplified') return 'combat'
      return 'combat'
    case 'goldPopup':
      return 'floater'
    default:
      return 'cosmetic'
  }
}

export function fxPriorityScore(fx: CombatFx): number {
  switch (fxPriorityBand(fx)) {
    case 'essential':
      return 100
    case 'signature':
      return 80
    case 'combat':
      return 50
    case 'floater':
      return 20
    default:
      return 10
  }
}

export interface FxBudgetPrefs {
  simulationSpeed: number
  reducedMotion: boolean
}

export interface FxLiveCounts {
  damageNumbers: number
  goldPopups: number
  hitFlashes: number
  particles: number
}

export const FX_BUDGET = {
  maxDamageNumbers: 14,
  /** Aggressive 3× floater cap — keep board readable under soup pressure. */
  maxDamageNumbersAt3x: 8,
  maxGoldPopups: 6,
  maxGoldPopupsAt3x: 3,
  maxHitFlashes: 12,
  maxHitFlashesAt3x: 6,
  maxParticles: 18,
  maxParticlesAt3x: 8,
  /** Drop DoT floaters below this amount at 3x. */
  minDotAmountAt3x: 4,
  /** Drop direct floaters below this at 3x when over budget. */
  minDirectAmountAt3x: 8
} as const

export function damageNumberCap(prefs: FxBudgetPrefs): number {
  return prefs.simulationSpeed >= 3 ? FX_BUDGET.maxDamageNumbersAt3x : FX_BUDGET.maxDamageNumbers
}

export function goldPopupCap(prefs: FxBudgetPrefs): number {
  return prefs.simulationSpeed >= 3 ? FX_BUDGET.maxGoldPopupsAt3x : FX_BUDGET.maxGoldPopups
}

export function hitFlashCap(prefs: FxBudgetPrefs): number {
  return prefs.simulationSpeed >= 3 ? FX_BUDGET.maxHitFlashesAt3x : FX_BUDGET.maxHitFlashes
}

export function particleCap(prefs: FxBudgetPrefs): number {
  return prefs.simulationSpeed >= 3 ? FX_BUDGET.maxParticlesAt3x : FX_BUDGET.maxParticles
}

/**
 * Decide whether a freshly consumed CombatFx should spawn a visual.
 * Essential/signature events always pass. Floaters respect live caps + 3x rules.
 */
export function admitCombatFx(
  fx: CombatFx,
  prefs: FxBudgetPrefs,
  live: FxLiveCounts
): boolean {
  const band = fxPriorityBand(fx)
  if (band === 'essential' || band === 'signature') return true

  if (fx.type === 'damageNumber') {
    if (prefs.simulationSpeed >= 3) {
      if (fx.style === 'dot' && fx.amount < FX_BUDGET.minDotAmountAt3x) return false
      if (
        live.damageNumbers >= damageNumberCap(prefs) &&
        fx.amount < FX_BUDGET.minDirectAmountAt3x
      ) {
        return false
      }
    }
    return live.damageNumbers < damageNumberCap(prefs)
  }

  if (fx.type === 'goldPopup') {
    return live.goldPopups < goldPopupCap(prefs)
  }

  if (fx.type === 'hitFlash') {
    return live.hitFlashes < hitFlashCap(prefs)
  }

  if (fx.type === 'impact') {
    return live.particles < particleCap(prefs)
  }

  return true
}

/** Merge nearby gold popups in a short window (presentation only). */
export interface GoldAggregate {
  x: number
  y: number
  amount: number
}

export function mergeGoldPopup(
  pending: GoldAggregate[],
  next: GoldAggregate,
  mergeRadius = 0.75
): GoldAggregate[] {
  const hit = pending.find(
    (entry) => Math.hypot(entry.x - next.x, entry.y - next.y) <= mergeRadius
  )
  if (hit) {
    hit.amount += next.amount
    hit.x = (hit.x + next.x) / 2
    hit.y = (hit.y + next.y) / 2
    return pending
  }
  return [...pending, { ...next }]
}

/** Duration scale for combat FX: shorter at high sim speed (already in scaleSimDurationMs). */
export function combatFxSimMs(
  baseMs: number,
  prefs: FxBudgetPrefs,
  essential = false
): number {
  let ms = baseMs / Math.max(1, prefs.simulationSpeed)
  if (prefs.reducedMotion) {
    ms = essential ? Math.min(ms, 140) : Math.min(ms, 90)
  }
  return Math.max(16, ms)
}
